import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface GrowthAgentDefinition {
  id: string;
  agentKey: string;
  name: string;
  agentType: string;
  description?: string | null;
  promptPackRef?: string | null;
  knowledgeScopeJson: string;
  defaultAutomationLevel: string;
  isActive: boolean;
  version: string;
}

export interface GrowthAgentRun {
  id: string;
  agentDefinitionId: string;
  agentKey: string;
  status: string;
  inputContextJson: string;
  outputSummary?: string | null;
  aiCorrelationId?: string | null;
  providerMode: string;
  citationRefsJson: string;
  guardrailResultJson: string;
  startedAtUtc: string;
  completedAtUtc?: string | null;
  idempotencyKey: string;
  correlationId?: string | null;
}

export interface GrowthAgentRecommendation {
  id: string;
  agentRunId: string;
  recommendationType: string;
  title: string;
  summary: string;
  payloadJson: string;
  confidence: number;
  riskBand: string;
  approvalState: string;
  workflowProcessInstanceId?: string | null;
  workflowTaskId?: string | null;
  approvedByUserId?: string | null;
  approvedAtUtc?: string | null;
  rejectedReason?: string | null;
  guardrailResultJson: string;
  citationRefsJson: string;
  createdAtUtc: string;
}

export interface GrowthStrategyArtifact {
  id: string;
  artifactType: string;
  title: string;
  version: string;
  status: string;
  artifactJson: string;
  citationRefsJson: string;
  sourceAgentRunId?: string | null;
  approvedByUserId?: string | null;
  approvedAtUtc?: string | null;
}

export interface GrowthAgentGuardrailPolicy {
  id: string;
  policyKey: string;
  name: string;
  appliesToAgentTypesJson: string;
  spendCap?: number | null;
  channelRestrictionsJson: string;
  consentRequired: boolean;
  requireWorkflowApproval: boolean;
  riskThreshold: number;
  isActive: boolean;
}

export interface GrowthAgentWorkflowBinding {
  id: string;
  recommendationType: string;
  workflowProcessDefinitionId?: string | null;
  fallbackBehavior: string;
  isActive: boolean;
  configurationJson: string;
}

@Injectable({ providedIn: 'root' })
export class GrowthAgentService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.apiBaseUrl}/api/v1/growth/agents`;

  listDefinitions(): Observable<GrowthAgentDefinition[]> {
    return this.http.get<GrowthAgentDefinition[]>(`${this.base}/definitions`);
  }

  runAgent(agentKey: string, body: {
    segmentId?: string | null;
    contentAssetId?: string | null;
    campaignId?: string | null;
    partyId?: string | null;
    industryKey?: string | null;
    scopeNotes?: string | null;
    idempotencyKey: string;
    correlationId?: string | null;
  }): Observable<{ run: GrowthAgentRun; isIdempotentReplay: boolean }> {
    return this.http.post<{ run: GrowthAgentRun; isIdempotentReplay: boolean }>(`${this.base}/${agentKey}/run`, body);
  }

  listRuns(status?: string): Observable<GrowthAgentRun[]> {
    let params = new HttpParams();
    if (status) params = params.set('status', status);
    return this.http.get<GrowthAgentRun[]>(`${this.base}/runs`, { params });
  }

  getRun(id: string): Observable<GrowthAgentRun> {
    return this.http.get<GrowthAgentRun>(`${this.base}/runs/${id}`);
  }

  listRecommendations(approvalState?: string): Observable<GrowthAgentRecommendation[]> {
    let params = new HttpParams();
    if (approvalState) params = params.set('approvalState', approvalState);
    return this.http.get<GrowthAgentRecommendation[]>(`${this.base}/recommendations`, { params });
  }

  getRecommendation(id: string): Observable<GrowthAgentRecommendation> {
    return this.http.get<GrowthAgentRecommendation>(`${this.base}/recommendations/${id}`);
  }

  requestApproval(id: string): Observable<GrowthAgentRecommendation> {
    return this.http.post<GrowthAgentRecommendation>(`${this.base}/recommendations/${id}/request-approval`, {});
  }

  approveRecommendation(id: string): Observable<GrowthAgentRecommendation> {
    return this.http.post<GrowthAgentRecommendation>(`${this.base}/recommendations/${id}/approve`, {});
  }

  rejectRecommendation(id: string, reason: string): Observable<GrowthAgentRecommendation> {
    return this.http.post<GrowthAgentRecommendation>(`${this.base}/recommendations/${id}/reject`, { reason });
  }

  listGuardrailPolicies(): Observable<GrowthAgentGuardrailPolicy[]> {
    return this.http.get<GrowthAgentGuardrailPolicy[]>(`${this.base}/guardrail-policies`);
  }

  upsertGuardrailPolicy(body: {
    policyKey: string;
    name: string;
    appliesToAgentTypesJson: string;
    spendCap?: number | null;
    channelRestrictionsJson: string;
    consentRequired: boolean;
    requireWorkflowApproval: boolean;
    riskThreshold: number;
    isActive: boolean;
  }): Observable<GrowthAgentGuardrailPolicy> {
    return this.http.post<GrowthAgentGuardrailPolicy>(`${this.base}/guardrail-policies`, body);
  }

  listWorkflowBindings(): Observable<GrowthAgentWorkflowBinding[]> {
    return this.http.get<GrowthAgentWorkflowBinding[]>(`${this.base}/workflow-bindings`);
  }

  upsertWorkflowBinding(body: {
    recommendationType: string;
    workflowProcessDefinitionId?: string | null;
    fallbackBehavior: string;
    isActive: boolean;
    configurationJson: string;
  }): Observable<GrowthAgentWorkflowBinding> {
    return this.http.put<GrowthAgentWorkflowBinding>(`${this.base}/workflow-bindings`, body);
  }

  listStrategyArtifacts(): Observable<GrowthStrategyArtifact[]> {
    return this.http.get<GrowthStrategyArtifact[]>(`${this.base}/strategy-artifacts`);
  }

  createStrategyArtifact(body: {
    artifactType: string;
    title: string;
    version: string;
    artifactJson: string;
    citationRefsJson: string;
    sourceAgentRunId?: string | null;
  }): Observable<GrowthStrategyArtifact> {
    return this.http.post<GrowthStrategyArtifact>(`${this.base}/strategy-artifacts`, body);
  }

  submitStrategyArtifact(id: string): Observable<GrowthStrategyArtifact> {
    return this.http.post<GrowthStrategyArtifact>(`${this.base}/strategy-artifacts/${id}/submit-for-approval`, {});
  }

  approveStrategyArtifact(id: string): Observable<GrowthStrategyArtifact> {
    return this.http.post<GrowthStrategyArtifact>(`${this.base}/strategy-artifacts/${id}/approve`, {});
  }

  updateStrategyArtifact(
    id: string,
    body: { title: string; version: string; artifactJson: string; citationRefsJson: string }
  ): Observable<GrowthStrategyArtifact> {
    return this.http.put<GrowthStrategyArtifact>(`${this.base}/strategy-artifacts/${id}`, body);
  }
}

export function parseJsonArray(json: string | null | undefined): unknown[] {
  if (!json?.trim()) return [];
  try {
    const parsed = JSON.parse(json) as unknown;
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}
